/**
 * BankPinAssignmentMatrix — Lift assignment for Sequent/Numato pin mappings.
 *
 * The Sequent equivalent of BankOffsetMatrix.  The adapter CDO defines
 * pin-to-message pairs (WHAT each pin does).  This component lets the user
 * assign a lift/car from the current bank to each pin (WHO it belongs to).
 *
 * Data lives in the adapter CDO's `discrete_2_message` array.  This component
 * receives the full array, shows every entry, and lets the user set the
 * lift_id + bank_id for entries belonging to this bank.
 *
 * Three assignment modes cover the patterns operators actually use:
 *   - single   — every pin in this bank belongs to the same car (the common
 *                case: one Sequent per bank, one car per bank).
 *   - split    — pins in this bank are distributed across the bank's cars.
 *   - advanced — rare: an individual pin is reassigned to a different bank
 *                entirely (the adapter's discrete_2_message array is shared
 *                across every bank the adapter serves).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Modal,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
} from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { DiscreteToLn2EntryInterface } from "../../../interfaces/Adapter.interface";
import {
  getDisplayName,
  messageContentToCategoryKey,
} from "../../../flatbuffers/types/ln2MessageOptions";
import { countPinsAssignedToBank, isAssignedToBank } from "./pinAssignmentCheck";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PinAssignmentLift {
  /** Real Lift UUID — never an index. */
  id: string;
  name: string;
  position: number;
}

export interface PinAssignmentBankOption {
  id: string;
  name: string;
}

export type PinAssignmentMode = "single" | "split" | "advanced";

export interface PinAssignmentMatrixProps {
  /** Full discrete_2_message array from the adapter CDO. */
  allEntries: DiscreteToLn2EntryInterface[];
  /** Called with the FULL updated array on any change. */
  onEntriesChange: (entries: DiscreteToLn2EntryInterface[]) => void;
  /** Current bank ID — entries assigned to this bank. */
  bankId: string;
  /** Current bank name — embedded in assigned entries. */
  bankName: string;
  /** Lifts/cars in this bank. */
  lifts: PinAssignmentLift[];
  /** Other banks this adapter serves — only selectable in "advanced" mode. */
  otherBanks?: PinAssignmentBankOption[];
  /** Show the merged-channel hint alert below the mode selector. */
  showMergedChannelHint?: boolean;
  /** Called when the user clicks "Set up merged channels" in the hint alert. */
  onRequestMergedChannels?: () => void;
}

interface PinRow {
  entry: DiscreteToLn2EntryInterface;
  idx: number;
  isMine: boolean;
  isOtherBank: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pinLabel(entry: DiscreteToLn2EntryInterface): string {
  return `B${entry.key.board_address}:P${entry.key.pin_num}`;
}

function messageLabel(entry: DiscreteToLn2EntryInterface): string {
  const m = entry.value.message;
  if (m.encoded_type === 0 && m.message_content === 0) return "—";
  const catKey = messageContentToCategoryKey(m.message_content);
  const variant = m.encoded_type === 1 ? "normal" : "valued";
  const compositeKey = `${catKey}:${variant}:${m.message_value}`;
  const display = getDisplayName(compositeKey);
  return display || `${catKey}:${m.message_value}`;
}

function polarityLabel(entry: DiscreteToLn2EntryInterface): string {
  return entry.value.active_high ? "HIGH -> active" : "LOW -> active";
}

/**
 * Initial mode, computed once from props on mount (LIFT-393 follow-up):
 *   - a single-car bank has nothing to choose between → single
 *   - any pin already pointing at a different bank → advanced (rare, but the
 *     data says it's in play — don't hide it)
 *   - this bank's pins already spread across 2+ distinct cars → split
 *   - otherwise → single, the common case
 */
export function computeInitialPinAssignmentMode(
  entries: readonly DiscreteToLn2EntryInterface[],
  bankId: string,
  lifts: readonly PinAssignmentLift[],
): PinAssignmentMode {
  if (lifts.length <= 1) return "single";

  const hasOtherBank = entries.some((e) => e.value.bank_id?.id && e.value.bank_id.id !== bankId);
  if (hasOtherBank) return "advanced";

  const distinctLiftIds = new Set(
    entries.filter((e) => isAssignedToBank(e, bankId)).map((e) => e.value.lift_id?.id),
  );
  if (distinctLiftIds.size >= 2) return "split";

  return "single";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BankPinAssignmentMatrix({
  allEntries,
  onEntriesChange,
  bankId,
  bankName,
  lifts,
  otherBanks = [],
  showMergedChannelHint = false,
  onRequestMergedChannels = undefined,
}: PinAssignmentMatrixProps) {
  // Categorize entries: this bank, other bank, unassigned
  const rows: PinRow[] = useMemo(
    () =>
      allEntries.map((entry, idx) => {
        const eid = entry.value.bank_id?.id;
        const isMine = eid === bankId;
        const isOtherBank = Boolean(eid) && eid !== bankId;
        return { entry, idx, isMine, isOtherBank };
      }),
    [allEntries, bankId],
  );

  const [mode, setMode] = useState<PinAssignmentMode>(() =>
    computeInitialPinAssignmentMode(allEntries, bankId, lifts),
  );
  const [selectedIdx, setSelectedIdx] = useState<Set<number>>(() => new Set());
  const [bulkLiftId, setBulkLiftId] = useState<string | undefined>(undefined);
  const autoAppliedOnceRef = useRef(false);

  const eligibleIdxs = useMemo(() => rows.filter((r) => !r.isOtherBank).map((r) => r.idx), [rows]);

  const boardAddresses = useMemo(
    () => Array.from(new Set(allEntries.map((e) => e.key.board_address))).sort((a, b) => a - b),
    [allEntries],
  );

  const assignedCount = useMemo(
    () => countPinsAssignedToBank(allEntries, bankId),
    [allEntries, bankId],
  );

  // The lift shared by every eligible (non-other-bank) row, if they all agree.
  const singleModeLiftId = useMemo(() => {
    const eligibleRows = rows.filter((r) => !r.isOtherBank);
    if (eligibleRows.length === 0) return undefined;
    const first = eligibleRows[0].entry.value.lift_id?.id;
    if (!first) return undefined;
    const allSame = eligibleRows.every(
      (r) => r.entry.value.bank_id?.id === bankId && r.entry.value.lift_id?.id === first,
    );
    return allSame ? first : undefined;
  }, [rows, bankId]);

  // With exactly one car, there is nothing to choose — show it pre-selected
  // even before the auto-apply effect below has run.
  // Only read inside the single-mode branch, so it needs no mode guard of its own.
  const displayedSingleLiftId = lifts.length === 1 ? lifts[0].id : singleModeLiftId;

  // ---------------------------------------------------------------------
  // Mutators — each builds a new array and calls onEntriesChange exactly once.
  // ---------------------------------------------------------------------

  const liftNameById = useMemo(() => new Map(lifts.map((l) => [l.id, l.name])), [lifts]);

  /**
   * The single definition of what "this pin belongs to this bank's car" looks
   * like. Every mutator below differs only in which entries it selects, so the
   * shape lives here once rather than being rebuilt at each call site.
   */
  const withLiftAssignment = (
    entry: DiscreteToLn2EntryInterface,
    liftId: string,
  ): DiscreteToLn2EntryInterface => ({
    ...entry,
    value: {
      ...entry.value,
      bank_id: { id: bankId, name: bankName },
      lift_id: { id: liftId, name: liftNameById.get(liftId) ?? "" },
    },
  });

  const assignLift = (globalIdx: number, liftId: string) => {
    onEntriesChange(
      allEntries.map((e, i) => (i === globalIdx ? withLiftAssignment(e, liftId) : e)),
    );
  };

  const clearLift = (globalIdx: number) => {
    const updated = allEntries.map((e, i) => {
      if (i !== globalIdx) return e;
      return {
        ...e,
        value: {
          ...e.value,
          bank_id: undefined,
          lift_id: undefined,
        },
      };
    });
    onEntriesChange(updated);
  };

  /** Assigns bank+lift to every entry not owned by another bank, in one call. */
  const applyLiftToAll = (liftId: string) => {
    onEntriesChange(
      allEntries.map((e) => {
        const isOther = Boolean(e.value.bank_id?.id) && e.value.bank_id?.id !== bankId;
        return isOther ? e : withLiftAssignment(e, liftId);
      }),
    );
  };

  /** Clears bank+lift on every entry currently owned by this bank. */
  const clearAllMine = () => {
    const updated = allEntries.map((e) => {
      if (e.value.bank_id?.id !== bankId) return e;
      return {
        ...e,
        value: {
          ...e.value,
          bank_id: undefined,
          lift_id: undefined,
        },
      };
    });
    onEntriesChange(updated);
  };

  const setBankForRow = (globalIdx: number, newBankId: string) => {
    const updated = allEntries.map((e, i) => {
      if (i !== globalIdx) return e;
      const name =
        newBankId === bankId ? bankName : (otherBanks.find((b) => b.id === newBankId)?.name ?? "");
      return {
        ...e,
        value: {
          ...e.value,
          bank_id: { id: newBankId, name },
          // A lift from the old bank is meaningless in the new one.
          lift_id: undefined,
        },
      };
    });
    onEntriesChange(updated);
  };

  // ---------------------------------------------------------------------
  // Bulk selection (split / advanced modes)
  // ---------------------------------------------------------------------

  const toggleRow = (idx: number, checked: boolean) => {
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(idx);
      } else {
        next.delete(idx);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedIdx(new Set(eligibleIdxs));
  const selectNone = () => setSelectedIdx(new Set());
  const selectUnassigned = () =>
    setSelectedIdx(
      new Set(rows.filter((r) => !r.isOtherBank && !r.entry.value.lift_id?.id).map((r) => r.idx)),
    );
  const selectBoard = (boardAddress: number) =>
    setSelectedIdx(
      new Set(
        rows
          .filter((r) => !r.isOtherBank && r.entry.key.board_address === boardAddress)
          .map((r) => r.idx),
      ),
    );

  const applyBulkAssign = () => {
    if (!bulkLiftId) return;
    const eligibleSelected = new Set(
      Array.from(selectedIdx).filter((i) => rows[i] && !rows[i].isOtherBank),
    );
    if (eligibleSelected.size === 0) return;
    onEntriesChange(
      allEntries.map((e, i) => (eligibleSelected.has(i) ? withLiftAssignment(e, bulkLiftId) : e)),
    );
    setSelectedIdx(new Set());
    setBulkLiftId(undefined);
  };

  /** Splits eligible pins across lifts contiguously by index; remainder on earlier lifts. */
  const distributeEvenly = () => {
    const eligible = rows.filter((r) => !r.isOtherBank).map((r) => r.idx);
    if (eligible.length === 0 || lifts.length === 0) return;

    const base = Math.floor(eligible.length / lifts.length);
    const remainder = eligible.length % lifts.length;
    const liftForPosition = lifts.flatMap((lift, li) =>
      Array<string>(base + (li < remainder ? 1 : 0)).fill(lift.id),
    );

    const idxToLift = new Map<number, string>();
    eligible.forEach((globalIdx, pos) => {
      idxToLift.set(globalIdx, liftForPosition[pos]);
    });

    onEntriesChange(
      allEntries.map((e, i) => {
        const liftId = idxToLift.get(i);
        return liftId ? withLiftAssignment(e, liftId) : e;
      }),
    );
  };

  // ---------------------------------------------------------------------
  // Mode switching — never silently destroy work.
  // ---------------------------------------------------------------------

  const requestModeChange = (nextMode: PinAssignmentMode) => {
    if (nextMode === mode) return;

    const mineAssigned = rows.filter((r) => isAssignedToBank(r.entry, bankId));
    const mineAssignedCount = mineAssigned.length;
    const distinctLiftIds = new Set(mineAssigned.map((r) => r.entry.value.lift_id?.id));
    const otherBankCount = rows.filter((r) => r.isOtherBank).length;

    const warnMultiLift = nextMode === "single" && distinctLiftIds.size >= 2;
    const warnOtherBank = mode === "advanced" && nextMode !== "advanced" && otherBankCount > 0;

    if (!warnMultiLift && !warnOtherBank) {
      setMode(nextMode);
      setSelectedIdx(new Set());
      return;
    }

    const lines: string[] = [];
    if (warnMultiLift) {
      lines.push(
        `${mineAssignedCount} pin${mineAssignedCount === 1 ? "" : "s"} currently ${
          mineAssignedCount === 1 ? "has" : "have"
        } different cars assigned. Switching to "All to one car" lets you overwrite them with a single car.`,
      );
    }
    if (warnOtherBank) {
      lines.push(
        `${otherBankCount} pin${otherBankCount === 1 ? "" : "s"} ${
          otherBankCount === 1 ? "is" : "are"
        } assigned to another bank and will be left untouched.`,
      );
    }

    Modal.confirm({
      title: "Switch assignment mode?",
      content: (
        <div>
          {lines.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      ),
      okText: "Switch mode",
      cancelText: "Cancel",
      onOk: () => {
        setMode(nextMode);
        setSelectedIdx(new Set());
      },
    });
  };

  // With exactly one car, there is only one valid assignment — apply it once
  // on mount rather than making the operator click through a single-option
  // dropdown. Guarded so it only ever runs once per mount.
  useEffect(() => {
    if (autoAppliedOnceRef.current) return;
    autoAppliedOnceRef.current = true;
    if (mode !== "single" || lifts.length !== 1) return;
    const onlyLift = lifts[0];
    const needsApply = rows.some(
      (r) =>
        !r.isOtherBank &&
        (r.entry.value.bank_id?.id !== bankId || r.entry.value.lift_id?.id !== onlyLift.id),
    );
    if (needsApply) applyLiftToAll(onlyLift.id);
    // Intentionally run once on mount only (guarded by autoAppliedOnceRef above),
    // not on every allEntries/lifts change — see the guard's comment.
  }, []);

  if (allEntries.length === 0) {
    return (
      <div style={{ color: "#888", padding: 8 }}>
        No pin mappings defined on the adapter. Configure pin-to-message mappings on the adapter CDO
        page first.
      </div>
    );
  }

  const headerStyle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 11,
    color: "#888",
    padding: "4px 12px",
    borderBottom: "1px solid #303030",
    marginBottom: 4,
  };

  const rowStyle = (isOtherBank: boolean): React.CSSProperties => ({
    padding: "6px 12px",
    borderBottom: "1px solid #1f1f1f",
    alignItems: "center",
    opacity: isOtherBank ? 0.5 : 1,
  });

  function renderStatusCell(row: PinRow): React.ReactNode {
    const { entry, isMine, isOtherBank } = row;
    if (isOtherBank) {
      return (
        <Tag color="orange" style={{ fontSize: 10 }}>
          Other Bank
        </Tag>
      );
    }
    if (isMine) {
      // Claimed by this bank but naming a car the bank does not have — a legacy
      // index id, or a car deleted since. Flagged per row so the warning above
      // the matrix is actionable rather than just a count.
      const liftId = entry.value.lift_id?.id;
      if (liftId && !liftNameById.has(liftId)) {
        return (
          <Tooltip
            title={`This pin names car "${liftId}", which is not one of this bank's cars. LiftCloud cannot resolve the id, so the pin is dropped from the generated CDO. Pick a car from the list to repair it.`}
          >
            <Tag color="red" style={{ fontSize: 10 }}>
              Unknown car
            </Tag>
          </Tooltip>
        );
      }
      return (
        <Tag color="blue" style={{ fontSize: 10 }}>
          This Bank
        </Tag>
      );
    }
    return <Tag style={{ fontSize: 10 }}>Unassigned</Tag>;
  }

  function renderLiftCell(row: PinRow): React.ReactNode {
    const { entry, idx, isOtherBank } = row;
    if (!isOtherBank) {
      return (
        <Select
          className="generalStyles__width100"
          size="small"
          value={row.isMine ? entry.value.lift_id?.id || undefined : undefined}
          placeholder="Unassigned"
          onChange={(val: string) => assignLift(idx, val)}
          allowClear
          onClear={() => clearLift(idx)}
        >
          {lifts.map((l) => (
            <Select.Option key={l.id} value={l.id}>
              {l.name}
            </Select.Option>
          ))}
        </Select>
      );
    }
    if (mode === "advanced") {
      return (
        <Tooltip title="This pin belongs to another bank — the lift list here only covers pins assigned to the currently selected bank.">
          <Select
            className="generalStyles__width100"
            size="small"
            disabled
            placeholder={entry.value.lift_id?.name || entry.value.bank_id?.name || "Other bank"}
            value={undefined}
          >
            {lifts.map((l) => (
              <Select.Option key={l.id} value={l.id}>
                {l.name}
              </Select.Option>
            ))}
          </Select>
        </Tooltip>
      );
    }
    return (
      <span style={{ fontSize: 11, color: "#666" }}>
        {entry.value.bank_id?.name || "Other bank"}
      </span>
    );
  }

  function renderSingleModeLiftCell(row: PinRow): React.ReactNode {
    if (row.isOtherBank) {
      return (
        <span style={{ fontSize: 11, color: "#666" }}>
          {row.entry.value.bank_id?.name || "Other bank"}
        </span>
      );
    }
    return <span style={{ fontSize: 12 }}>{row.entry.value.lift_id?.name || "—"}</span>;
  }

  const hasMineAssignment = rows.some((r) => r.isMine);
  const eligibleCount = eligibleIdxs.length;

  return (
    <div>
      <Segmented
        options={[
          { label: "All to one car", value: "single" },
          { label: "Split across cars", value: "split" },
          { label: "Advanced", value: "advanced" },
        ]}
        value={mode}
        onChange={(val) => requestModeChange(val as PinAssignmentMode)}
      />

      {showMergedChannelHint && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={`This bank has ${lifts.length} cars. If each car has its own Sequent at its own IP address, use a merged channel with one endpoint per car.`}
          action={
            onRequestMergedChannels && (
              <Button size="small" type="primary" onClick={onRequestMergedChannels}>
                Set up merged channels
              </Button>
            )
          }
        />
      )}

      <Divider orientation="left" plain>
        Pin → Lift Assignment ({assignedCount} of {allEntries.length} assigned to this bank)
      </Divider>

      {mode === "single" && (
        <div style={{ marginBottom: 16 }}>
          <Space align="center" wrap>
            <span>All {eligibleCount} pins belong to:</span>
            <Select
              style={{ minWidth: 220 }}
              size="small"
              placeholder="Select a car"
              value={displayedSingleLiftId}
              disabled={lifts.length === 1}
              onChange={(val: string) => applyLiftToAll(val)}
            >
              {lifts.map((l) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
            {displayedSingleLiftId && eligibleCount > 0 && (
              <Tag color="success">✓ applied to all {eligibleCount} pins</Tag>
            )}
            <Button size="small" type="link" onClick={clearAllMine} disabled={!hasMineAssignment}>
              Clear all
            </Button>
          </Space>
        </div>
      )}

      {(mode === "split" || mode === "advanced") && (
        <div style={{ marginBottom: 12, padding: 8, border: "1px solid #303030", borderRadius: 4 }}>
          <Space wrap size={[8, 8]}>
            <Button size="small" onClick={selectAll}>
              All
            </Button>
            <Button size="small" onClick={selectNone}>
              None
            </Button>
            <Button size="small" onClick={selectUnassigned}>
              Unassigned
            </Button>
            {boardAddresses.map((board) => (
              <Button size="small" key={board} onClick={() => selectBoard(board)}>
                Board {board}
              </Button>
            ))}
            <Divider type="vertical" />
            <Select
              size="small"
              style={{ minWidth: 160 }}
              placeholder="Assign selected to"
              value={bulkLiftId}
              onChange={(val: string) => setBulkLiftId(val)}
              allowClear
            >
              {lifts.map((l) => (
                <Select.Option key={l.id} value={l.id}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
            <Button
              size="small"
              type="primary"
              disabled={!bulkLiftId || selectedIdx.size === 0}
              onClick={applyBulkAssign}
            >
              Apply
            </Button>
            <Button size="small" onClick={distributeEvenly}>
              Distribute evenly
            </Button>
          </Space>
          <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
            {selectedIdx.size} selected
          </div>
        </div>
      )}

      {/* Header */}
      {mode === "single" ? (
        <div className="row" style={headerStyle}>
          <div className="col-2">Pin</div>
          <div className="col-2">Polarity</div>
          <div className="col-4">LN2 Message</div>
          <div className="col-2">Lift / Car</div>
          <div className="col-2">Status</div>
        </div>
      ) : (
        <div className="row" style={headerStyle}>
          <div className="col-1">
            <Checkbox
              checked={eligibleIdxs.length > 0 && selectedIdx.size === eligibleIdxs.length}
              indeterminate={selectedIdx.size > 0 && selectedIdx.size < eligibleIdxs.length}
              onChange={(e: CheckboxChangeEvent) => (e.target.checked ? selectAll() : selectNone())}
            />
          </div>
          <div className="col-1">Pin</div>
          <div className={mode === "advanced" ? "col-1" : "col-2"}>Polarity</div>
          <div className={mode === "advanced" ? "col-3" : "col-4"}>LN2 Message</div>
          {mode === "advanced" && <div className="col-2">Bank</div>}
          <div className="col-2">Lift / Car</div>
          <div className="col-2">Status</div>
        </div>
      )}

      {rows.map((row) => {
        const { entry, idx, isOtherBank } = row;
        if (mode === "single") {
          return (
            <div key={idx} className="row" style={rowStyle(isOtherBank)}>
              <div className="col-2">
                <span style={{ fontFamily: "monospace", fontSize: 12 }}>{pinLabel(entry)}</span>
              </div>
              <div className="col-2">
                <Tag color={entry.value.active_high ? "green" : "orange"}>
                  {polarityLabel(entry)}
                </Tag>
              </div>
              <div className="col-4">
                <span style={{ fontSize: 12 }}>{messageLabel(entry)}</span>
              </div>
              <div className="col-2">{renderSingleModeLiftCell(row)}</div>
              <div className="col-2">{renderStatusCell(row)}</div>
            </div>
          );
        }

        return (
          <div key={idx} className="row" style={rowStyle(isOtherBank)}>
            <div className="col-1">
              <Checkbox
                disabled={isOtherBank}
                checked={!isOtherBank && selectedIdx.has(idx)}
                onChange={(e: CheckboxChangeEvent) => toggleRow(idx, e.target.checked)}
              />
            </div>
            <div className="col-1">
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>{pinLabel(entry)}</span>
            </div>
            <div className={mode === "advanced" ? "col-1" : "col-2"}>
              <Tag color={entry.value.active_high ? "green" : "orange"}>{polarityLabel(entry)}</Tag>
            </div>
            <div className={mode === "advanced" ? "col-3" : "col-4"}>
              <span style={{ fontSize: 12 }}>{messageLabel(entry)}</span>
            </div>
            {mode === "advanced" && (
              <div className="col-2">
                <Select
                  className="generalStyles__width100"
                  size="small"
                  value={entry.value.bank_id?.id}
                  placeholder="Unassigned"
                  onChange={(val: string) => setBankForRow(idx, val)}
                  allowClear
                  onClear={() => clearLift(idx)}
                >
                  <Select.Option key={bankId} value={bankId}>
                    {bankName}
                  </Select.Option>
                  {otherBanks.map((b) => (
                    <Select.Option key={b.id} value={b.id}>
                      {b.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
            <div className="col-2">{renderLiftCell(row)}</div>
            <div className="col-2">{renderStatusCell(row)}</div>
          </div>
        );
      })}
    </div>
  );
}
